import FileSaver from "file-saver";
import { Configuration, Module, Project, YUIContext } from "./types/type_def";
import jsPDF from "jspdf";
import { materialLibrary, productLibrary, seriesLibrary } from "./libraries";
import OpenSansRegular from "./fonts/OpenSans-Regular-normal.js";
import OpenSansBold from "./fonts/OpenSans-Bold-normal.js";
import { FormatHelper, ModuleHelper } from "./helpers";
import { DragDropManager, Draggable, Droppable, Feedback, PointerActivationConstraints, PointerSensor } from "@dnd-kit/dom";
import { RestrictToWindow } from "@dnd-kit/dom/modifiers";

/**
 * DragDropHandler 
 */

export class DragDropHandler {

    manager: DragDropManager

    constructor() {        
        this.manager = new DragDropManager({
            modifiers: (defaults) => [...defaults, RestrictToWindow],
            sensors:  (defaults) => [
                ...defaults,
                PointerSensor.configure({
                    activationConstraints(event, source) {
                        const {pointerType} = event;

                        switch (pointerType) {
                        case 'mouse':
                            return [
                                new PointerActivationConstraints.Delay({value: 100, tolerance: 10}),                              
                            ];
                        case 'touch':
                            return [
                                new PointerActivationConstraints.Delay({value: 100, tolerance: 10}),
                            ];
                        default:
                            return [
                                new PointerActivationConstraints.Delay({value: 100, tolerance: 10}),
                                new PointerActivationConstraints.Distance({value: 5}),
                            ];
                        }
                    }
                })
            ],
            plugins: (defaults) => [
                ...defaults,
                Feedback.configure({                  
                    feedback: 'clone',
                    dropAnimation: null
                })               
            ],
        });
    }

    createDraggable(id: string, element: HTMLElement, handleElement?: HTMLElement) {
        const draggable = new Draggable({
            id: id,
            element,            
            type: "product"                    
        }, this.manager);

        if (handleElement)  
            draggable.handle = handleElement;
        
        return draggable;
    }

    createDroppable(id: string, element: HTMLElement) {        
        const droppable = new Droppable({
            id: id,
            element,
            accept: ["product"]
        }, this.manager)
        return droppable;
    }

}

/*
 * Error Handler
 * */

export class ErrorHandler {

    Y: YUIContext
    errors: Array<string>
    errorContainer: any
    errorItems: any

    constructor(Y: YUIContext, errorContainer: any) {

        this.Y = Y as YUIContext;
        this.errors = [] as Array<string>;
        this.errorContainer = Y.one(errorContainer);
        this.errorItems = this.errorContainer.one(".errors__items");
       
        this.errorContainer.delegate("click", function (e) {

            e.preventDefault();           

            var errorItems = Y.one(".errors__items");
            if (!errorItems.hasClass("is-active")) {
                Y.one(".errors__items").addClass("is-active");
            } else {
                Y.one(".errors__items").removeClass("is-active");
            }

        }, ".errors__button");
    }

    addError(error: string) {
        if (!this.errors.includes(error)) {
            this.errors.push(error);
        }
        this.displayErrors();       
    }

    resetErrors() {
        this.errors = [];
        this.displayErrors()
    }

    displayErrors() {

        var self = this;

        this.errorContainer.removeClass("is-active");
        this.errorItems.removeClass("is-active");

        if (this.errors.length > 0) {

            this.errorItems.empty();

            this.errors.forEach(function (error) {
                let errorItemNode = self.Y.Node.create("<li>");
                errorItemNode.addClass("errors__items__item notification");
                errorItemNode.setHTML(error);
                self.errorItems.appendChild(errorItemNode);
            });

            this.errorContainer.addClass("is-active");        
            this.errorItems.addClass("is-active");              
        }

        window.dispatchEvent(new Event("error"));

    }
}

/*
 * PDF Handler
 * */


export class PDFHandler {
    pageWidth = 210;
    pageHeight = 297;
    margin = 10;


    private addFooters(doc: jsPDF, project: Project){
        const pageCount = doc.getNumberOfPages();
        this.setFont(doc, false, 8);
        for (var i = 1; i <= pageCount; i++) {
            doc.setPage(i)
            doc.text(`project: ${project.name}`, this.margin, this.pageHeight - 16);
            doc.text(`confignumber: ${project.identifier.toUpperCase()}`, this.margin, this.pageHeight - 10);

            doc.text(`${new Date().toLocaleDateString("en-GB")} ${new Date().toLocaleTimeString("en-GB")}`, this.pageWidth - this.margin, this.pageHeight - 16, {align: "right"});
            doc.text(`page: ${i}/${pageCount}`, this.pageWidth - this.margin, this.pageHeight - 10, {align: "right"});
        }
    }

    private setFont(doc: jsPDF, isBold: boolean, size: number) {
        doc.setFont("OpenSans", "normal", isBold ? "bold" : "normal");
        doc.setFontSize(size);
    }

    async generatePDF(project: Project, configuration: Configuration, moduleHelper: ModuleHelper, module: Module) {
        let currentMaterial = materialLibrary.getMaterialByProductNumber(configuration.data.material);
        var series = await seriesLibrary.fetchSeriesByName(configuration.series);
        var summaryList = await moduleHelper.generateModuleSummaryList(module, configuration);
        var totalPrice = await moduleHelper.getTotalModulePrice(module, configuration);
        let currency = summaryList.elements[0].currency;

        var leg = series.legPackages.find(e => e.identifier === configuration.data.leg);

        const doc = new jsPDF('p','mm','a4');

        
        doc.addFileToVFS('OpenSans-Regular.ttf', OpenSansRegular)
        doc.addFileToVFS('OpenSans-Bold.ttf', OpenSansBold)
        doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal')
        doc.addFont('OpenSans-Bold.ttf', 'OpenSans', 'bold')

        //frontpage
        if(configuration.image){
            doc.addImage(configuration.image, "JPEG", 0, 0, this.pageWidth, this.pageWidth / 2);
        }

        let y = this.pageWidth / 2 + 20;

        this.setFont(doc, true, 10);
        doc.text("Sofa series:", this.margin, y);
        y += 6;

        this.setFont(doc, false, 10);
        doc.text(configuration.series, this.margin, y);
        y += 10;

        this.setFont(doc, true, 10);
        doc.text("Fabric selection:", this.margin, y);
        y += 6;

        this.setFont(doc, false, 10);
        doc.text(currentMaterial.name, this.margin, y);
        y += 10;

        this.setFont(doc, true, 10);
        doc.text("Leg selection:", this.margin, y);
        y += 6;

        this.setFont(doc, false, 10);
        doc.text(leg?.name ?? "", this.margin, y);
        y += 10;

        this.setFont(doc, true, 10);
        doc.text("Options:", this.margin, y);
        y += 6;

        this.setFont(doc, false, 10);
        for (let i = 0; i < summaryList.customOptions.length; i++) {
            doc.text(summaryList.customOptions[i].productNo, this.margin, y);
            y += 6;
        }

        //partslist
        doc.addPage();
        
        let elements: {name: string, axNumber: string, productNumber: string, amount: string, price: string, totalPrice: string}[] = [];
        let divider = {name: "", axNumber: "", productNumber: "", amount: "", price: "", totalPrice: ""};

        summaryList.elements.forEach(summaryItem => {
            var product = productLibrary.getProductByProductNo(summaryItem.productNo);
            const price = summaryItem.price + summaryItem.extras.reduce((p, n) => { return p + n.price }, 0)
            
            elements.push({
                name: product.name,
                axNumber: summaryItem.axProductNo,
                price: FormatHelper.formatNumber(summaryItem.price) + " " + currency,
                totalPrice: FormatHelper.formatNumber(price) + " " + currency,
                productNumber: summaryItem.variantProductNo,
                amount: summaryItem.amount.toString(),
            });
            
            summaryItem.extras.forEach(extra => {
                elements.push({
                    name: extra.name,
                    axNumber: " ",
                    price: FormatHelper.formatNumber(extra.price) + " " + currency,
                    totalPrice: " ",
                    productNumber: extra.productNo + " ",
                    amount: " ",
                });
            });

            elements.push(divider);
        });

        summaryList.customOptions.forEach(customOption => {
            elements.push({
                name: customOption.productNo ,
                axNumber: " ",
                price:  " ",
                totalPrice: FormatHelper.formatNumber(customOption.totalprice) + " " + currency,
                productNumber: " ",
                amount: customOption.amount.toString(),
            });
        });

        summaryList.accessories.forEach(addOn => {
            const price = addOn.price + addOn.extras.reduce((p, n) => { return p + n.price }, 0)

            if(addOn.amount > 0){
                elements.push({
                    name: addOn.name,
                    amount: addOn.amount.toString(),
                    axNumber: " ",
                    price: FormatHelper.formatNumber(price) + " " + currency,
                    totalPrice: FormatHelper.formatNumber(price * addOn.amount) + " " + currency,
                    productNumber: addOn.productNo
                });
            }
        });

        elements.push({
            name: "Total",
            axNumber: " ",
            price: " ",
            totalPrice: FormatHelper.formatNumber(totalPrice.totalPrice) + " " + currency,
            productNumber: " ",
            amount: " ",
        });

        doc.setDrawColor("#aaa");
        doc.table(this.margin, this.margin, elements, [
            {align: "left", name: "name", padding: 0.5, prompt: "Name", width: 75},
            {align: "left", name: "axNumber", padding: 0.5, prompt: "Price list ID", width: 35},
            {align: "left", name: "productNumber", padding: 0.5, prompt: "Product Number", width: 60},
            {align: "left", name: "amount", padding: 0.5, prompt: "Quantity", width: 24},
            {align: "right", name: "price", padding: 0.5, prompt: "Price", width: 30},
            {align: "right", name: "totalPrice", padding: 0.5, prompt: "Total Price", width: 30}
        ], {
            fontSize: 7,
            headerBackgroundColor: "#fff",
            padding: 0.75,
        });



        //drawing
        if(configuration.drawing){
            doc.addPage();
            doc.addImage(configuration.drawing, "JPEG", 0, 0, this.pageWidth, this.pageWidth);
        }


        this.addFooters(doc, project);


        var pdfBlob = doc.output("blob");

        FileSaver.saveAs(pdfBlob, "order.pdf");
    }
}